import { useMarketTradeModal } from '@/hooks/shared/useMarketTradeModal';
import { useCryptoMarketsInfinite, useMarketsInfinite } from '@/services';
import { useCryptoFinanceSidebar } from '@/services/query/cryptoFinance.queries';
import { type CategoryType } from '@/types';
import { useCryptoFilters } from './useCryptoFilters';

export const useCryptoFinance = (category: CategoryType) => {
  // --- 1. Shared Infrastructure ---
  const { tradeModalState, handleOpenTradeModal, handleCloseTradeModal } = useMarketTradeModal();

  const {
    selectedFilter,
    sortBy,
    setSortBy,
    status,
    setStatus,
    filterParams,
    sortParams,
    handleFilterClick,
    handleResetFilters,
  } = useCryptoFilters(category);

  // --- 2. Module State ---
  const {
    data: sidebarFilters = [],
    isLoading: isSidebarLoading,
    isError: isSidebarError,
    refetch: refetchSidebar,
  } = useCryptoFinanceSidebar(category);

  const isCrypto = category === 'crypto';

  // --- 3. Data Fetching ---
  const cryptoQuery = useCryptoMarketsInfinite(
    {
      ...filterParams,
      ...sortParams,
    },
    { enabled: isCrypto && !isSidebarLoading }
  );

  const marketsQueryStandard = useMarketsInfinite(
    {
      ...filterParams,
      ...sortParams,
      active: status === 'Active',
      closed: status === 'Closed' || status === 'Resolved',
    },
    { enabled: !isCrypto && !isSidebarLoading }
  );

  const marketsQuery = isCrypto ? cryptoQuery : marketsQueryStandard;
  const events = marketsQuery.data?.pages.flatMap((page) => page.items) || [];

  // --- 4. Handlers ---

  const handleTradeClick = (marketId: string, outcomeId: string, isBuyOrder: boolean) => {
    const foundMarket = events
      .flatMap((event) => event.markets || [])
      .find((m: any) => m.id === marketId);
    if (!foundMarket) return;

    let outcomes: any[] = [];
    try {
      outcomes =
        typeof foundMarket.outcomes === 'string'
          ? JSON.parse(foundMarket.outcomes)
          : foundMarket.outcomes;
    } catch {
      outcomes = [];
    }

    const foundOutcome = Array.isArray(outcomes) ? outcomes.find((o) => o.id === outcomeId) : null;
    if (!foundOutcome) return;

    handleOpenTradeModal(foundMarket as any, foundOutcome as any, isBuyOrder);
  };

  const isLoading = marketsQuery.isLoading || isSidebarLoading;
  const error = marketsQuery.error || (isSidebarError ? new Error('Failed to load sidebar') : null);

  return {
    state: {
      sidebarFilters,
      isSidebarLoading,
      isSidebarError,
      selectedFilter,
      sortBy,
      status,
      tradeModalState,
      events,
      isLoading,
      error,
    },
    handlers: {
      handleFilterClick,
      setSortBy,
      setStatus,
      handleResetFilters,
      handleTradeClick,
      handleTradeModalClose: handleCloseTradeModal,
      refetch: marketsQuery.refetch,
      refetchSidebar,
      fetchNextPage: marketsQuery.fetchNextPage,
    },
    pagination: {
      hasNextPage: marketsQuery.hasNextPage,
      isFetchingNextPage: marketsQuery.isFetchingNextPage,
    },
  };
};
